import { memo, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import styles from "../../styles/case-manager-modules/patientCompletion.module.css";

const totalValue = 100;
const grayBackground = "#E0E0E0";

const PatientCompletion = ({ patients = [] }) => {
  const chartData = useMemo(() => {
    const total = patients.length;

    const outOfRange = patients.filter((p) => p.out_of_range).length;
    const outOfRangeCompletedPercentage = total
      ? (((total - outOfRange) / total) * 100).toFixed(1)
      : 0;
    const outOfRangeDetails = `${outOfRange} Out of Range Patients`;

    const needCallPatients = patients.filter((patient) => {
      return (
        patient.statuses &&
        patient.statuses.some(
          (status) =>
            status.status === "Need Call" || status.status === "Needs call"
        )
      );
    });

    const needCallCount = needCallPatients.length;
    const pendingCallsPercentage = total
      ? Math.round((needCallCount / total) * 100)
      : 0;
    const pendingCallsDetails = `${needCallCount} Patients Need Calls`;

    const completedCallPatients = patients.filter((patient) => {
      return (
        patient.statuses &&
        patient.statuses.some(
          (status) =>
            status.status === "Call completed" ||
            status.status === "Finalized" ||
            status.status === "Call Complete"
        )
      );
    });

    const completedCallCount = completedCallPatients.length;
    const completedCallsPercentage = total
      ? Math.round((completedCallCount / total) * 100)
      : 0;
    const remainingCallsCount = total - completedCallCount - needCallCount;
    const completedCallsDetails = `${remainingCallsCount} Calls Remaining`;

    const compliantPatients = patients.filter((p) => p.is_compliant).length;
    const compliantPercentage = total
      ? Math.round((compliantPatients / total) * 100)
      : 0;
    const compliantDetails = `${compliantPatients} Compliant Patients`;

    return [
      {
        name: " Compliance",
        value: compliantPercentage,
        color: "#C3E3FA",
        details: compliantDetails,
      },
      {
        name: " Pending Calls",
        value: pendingCallsPercentage,
        color: "#8ECBF6",
        details: pendingCallsDetails,
      },
      {
        name: " Calls Completed",
        value: completedCallsPercentage,
        color: "#5DA6F2",
        details: completedCallsDetails,
      },
      {
        name: " Out of Range Completed",
        value: outOfRangeCompletedPercentage,
        color: "#0D1440",
        details: outOfRangeDetails,
      },
    ];
  }, [patients]);

  return (
    <div className={styles.container}>
      <div className={styles.chart_section}>
        <PieChart width={350} height={330}>
          {chartData.map((entry, index) => {
            const innerRadius = 60 + index * 25;
            const outerRadius = innerRadius + 12;

            return (
              <>
                <Pie
                  key={`bg-${index}`}
                  data={[{ value: totalValue }]}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  isAnimationActive={false}
                >
                  <Cell fill={grayBackground} />
                </Pie>

                <Pie
                  key={`fg-${index}`}
                  data={[entry]}
                  cx="50%"
                  cy="50%"
                  innerRadius={innerRadius}
                  outerRadius={outerRadius}
                  startAngle={90}
                  endAngle={90 - (entry.value / totalValue) * 360}
                  dataKey="value"
                  isAnimationActive={false}
                  cornerRadius={10}
                >
                  <Cell fill={entry.color} />
                </Pie>
              </>
            );
          })}
          <Tooltip />
        </PieChart>
        <p className={styles.chart_label}>
          Patient <br /> Completion
        </p>
      </div>

      <div className={styles.data_section}>
        {chartData.map((entry, index) => (
          <div key={index} className={styles.data_item}>
            <span
              className={styles.color_box}
              style={{ backgroundColor: entry.color }}
            ></span>
            <div>
              <p className={styles.data_text}>
                <strong>
                  {entry.value}% &nbsp;{entry.name}
                </strong>
              </p>

              <p className={styles.data_details}>{entry.details}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default memo(PatientCompletion);
